import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  showHome?: boolean;
  className?: string;
}

// Route-to-label mapping for auto-generation
const routeLabels: Record<string, string> = {
  estimates: "Estimates",
  create: "Create",
  edit: "Edit",
  view: "View",
  invoices: "Invoices",
  projects: "Projects",
  customers: "Customers",
  suppliers: "Suppliers",
  settings: "Settings",
  dashboard: "Dashboard",
  "cost-center": "Cost Center",
  "internal-line-items": "Line Items",
  "system-items": "System Items",
  "line-items": "Line Items",
  assemblies: "Assemblies",
  credits: "Credit Notes",
  admin: "Admin",
  users: "Users",
  companies: "Companies",
};

/**
 * Breadcrumbs - Navigation breadcrumb trail
 * 
 * Can be used in two ways:
 * 1. Auto-generated from current route (default)
 * 2. Manually specified items
 * 
 * @example
 * // Auto-generated from route
 * <Breadcrumbs />
 * 
 * // Manual items
 * <Breadcrumbs items={[
 *   { label: "Estimates", href: "/estimates" },
 *   { label: "EST-001" }
 * ]} />
 */
const Breadcrumbs = ({
  items,
  showHome = true,
  className,
}: BreadcrumbsProps) => {
  const location = useLocation();

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Detect a supplier UUID in the path (e.g. /cost-center/suppliers/<uuid>/...)
  const supplierUuidIndex = pathSegments.findIndex(
    (seg, i) => uuidRe.test(seg) && pathSegments[i - 1] === "suppliers"
  );
  const supplierId = supplierUuidIndex >= 0 ? pathSegments[supplierUuidIndex] : null;

  const supplierQuery = useQuery({
    queryKey: ["breadcrumb-supplier", supplierId],
    enabled: !!supplierId && !items,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("name")
        .eq("id", supplierId!)
        .maybeSingle();
      if (error) throw error;
      return data?.name ?? null;
    },
  });

  // Auto-generate breadcrumbs from route if items not provided
  const breadcrumbItems = React.useMemo(() => {
    if (items) return items;

    const generatedItems: BreadcrumbItem[] = [];

    let currentPath = "";
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;

      // Skip UUIDs (they're typically entity IDs)
      const isUuid = uuidRe.test(segment);

      if (isUuid) {
        // Supplier UUID → show supplier name, link to supplier details page
        if (index === supplierUuidIndex) {
          generatedItems.push({
            label: supplierQuery.data ?? "Supplier",
            href: isLast ? undefined : `/cost-center/suppliers/${segment}`,
          });
        } else {
          generatedItems.push({
            label: "Details",
            href: isLast ? undefined : currentPath,
          });
        }
      } else {
        const label = routeLabels[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
        generatedItems.push({
          label,
          href: isLast ? undefined : currentPath,
        });
      }
    });

    return generatedItems;
  }, [items, location.pathname, supplierQuery.data, supplierUuidIndex]);

  if (breadcrumbItems.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn("flex items-center text-sm text-muted-foreground", className)}
    >
      <ol className="flex items-center gap-1 flex-wrap">
        {/* Home link */}
        {showHome && (
          <li className="flex items-center">
            <Link
              to="/"
              className="hover:text-foreground transition-colors p-1 -m-1"
              aria-label="Home"
            >
              <Home className="h-4 w-4" />
            </Link>
            {breadcrumbItems.length > 0 && (
              <ChevronRight className="h-4 w-4 mx-1 flex-shrink-0" />
            )}
          </li>
        )}

        {/* Breadcrumb items */}
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;

          return (
            <li key={index} className="flex items-center">
              {item.href && !isLast ? (
                <Link
                  to={item.href}
                  className="hover:text-foreground transition-colors truncate max-w-[150px]"
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  className={cn(
                    "truncate max-w-[200px]",
                    isLast && "text-foreground font-medium"
                  )}
                  aria-current={isLast ? "page" : undefined}
                >
                  {item.label}
                </span>
              )}
              {!isLast && (
                <ChevronRight className="h-4 w-4 mx-1 flex-shrink-0" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
